import React, { useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PresentationSlide } from '../types';
import { isLinkedLibrarySlide } from '../librarySlides';
import { isProductLinkedSlide } from '../pageKind';
import { COPY, deleteSlidesLabel } from '../presentationCopy';
import { SlideSelectModifiers } from './slideStripSelection';
import { PRES, TYPE } from '../chrome';
import { LibraryFollowCorner, Pill, ProductKindMark, SlideThumbnail } from '../ui';
import { focusVisibleSx } from '../ui/focus';
import {
  clampStripWidth,
  readStoredStripWidth,
  writeStoredStripWidth,
} from './slideOrder';

interface SlideStripProps {
  slides: PresentationSlide[];
  activeSlideId: string | null;
  selectedSlideIds?: string[];
  pageFormat?: string | null;
  readOnly?: boolean;
  showPageKindMarks?: boolean;
  onSelect: (slideId: string, modifiers?: SlideSelectModifiers) => void;
  onAdd: () => void;
  onDelete: (slideId: string) => void;
  onDuplicate: (slideId: string) => void;
  onToggleHidden: (slideId: string) => void;
  onToggleLock: (slideId: string) => void;
  canSaveToLibrary?: boolean;
  onSaveToLibrary?: (slideId: string) => void;
  canAddAsTemplate?: boolean;
  onAddAsTemplate?: (slideId: string) => void;
  canCopyLayoutToKit?: boolean;
  onCopyLayoutToKit?: (slideId: string) => void;
  onMoveLayoutToKit?: (slideId: string) => void;
}

interface SlideThumbProps {
  slide: PresentationSlide;
  index: number;
  active: boolean;
  selected: boolean;
  canDelete: boolean;
  pageFormat?: string | null;
  readOnly: boolean;
  showPageKindMarks?: boolean;
  onSelect: (slideId: string, modifiers?: SlideSelectModifiers) => void;
  onDelete: (slideId: string) => void;
  onDuplicate: (slideId: string) => void;
  onToggleHidden: (slideId: string) => void;
  onToggleLock: (slideId: string) => void;
  onOpenMenu: (slideId: string, position: { x: number; y: number }) => void;
}

const thumbActionSx = {
  bgcolor: PRES.surface,
  color: PRES.textPrimary,
  width: 22,
  height: 22,
  boxShadow: '0 1px 4px rgba(20,22,28,0.16)',
  '&:hover': { bgcolor: PRES.surfaceSunken },
} as const;

export function areSlideThumbPropsEqual(prev: SlideThumbProps, next: SlideThumbProps): boolean {
  return prev.slide === next.slide
    && prev.index === next.index
    && prev.active === next.active
    && prev.selected === next.selected
    && prev.canDelete === next.canDelete
    && prev.readOnly === next.readOnly
    && prev.onSelect === next.onSelect
    && prev.onDelete === next.onDelete
    && prev.onDuplicate === next.onDuplicate
    && prev.onToggleHidden === next.onToggleHidden
    && prev.onToggleLock === next.onToggleLock
    && prev.onOpenMenu === next.onOpenMenu
    && prev.pageFormat === next.pageFormat
    && prev.showPageKindMarks === next.showPageKindMarks;
}

function SlideThumbRow({
  slide,
  index,
  active,
  selected,
  canDelete,
  readOnly,
  onSelect,
  onDelete,
  onDuplicate,
  onToggleHidden,
  onToggleLock,
  onOpenMenu,
  pageFormat,
  showPageKindMarks = true,
}: SlideThumbProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({
    id: slide.slideId,
    disabled: readOnly,
  });
  const hidden = Boolean(slide.hidden);
  const locked = Boolean(slide.locked);

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...listeners}
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!selected) onSelect(slide.slideId);
        onOpenMenu(slide.slideId, { x: event.clientX, y: event.clientY });
      }}
      onClick={(event) => {
        if (event.shiftKey) event.preventDefault();
        onSelect(slide.slideId, {
          additive: event.metaKey || event.ctrlKey,
          range: event.shiftKey,
        });
      }}
      sx={{
        mb: 1.25,
        cursor: readOnly ? 'pointer' : 'grab',
        position: 'relative',
        opacity: isDragging ? 0.55 : 1,
        outline: isOver && !isDragging ? `2px solid ${PRES.interactive}` : 'none',
        outlineOffset: 2,
        '&:hover .slide-actions, &:focus-within .slide-actions': { opacity: 1 },
        '&:active': { cursor: readOnly ? 'pointer' : 'grabbing' },
      }}
    >
      <Box sx={{ position: 'relative' }}>
      <SlideThumbnail slide={slide} selected={selected} hidden={hidden} pageFormat={pageFormat} />
      {showPageKindMarks && isLinkedLibrarySlide(slide) && <LibraryFollowCorner />}
      {showPageKindMarks && isProductLinkedSlide(slide) && (
        <Box sx={{ position: 'absolute', left: 6, bottom: 6, zIndex: 2 }}>
          <ProductKindMark />
        </Box>
      )}
      {(hidden || locked) && (
        <Box sx={{ position: 'absolute', left: 6, top: showPageKindMarks && isLinkedLibrarySlide(slide) ? 48 : 6, display: 'flex', gap: 0.4, zIndex: 2 }}>
          {hidden && <Pill tone="warn" icon={<VisibilityOffOutlinedIcon sx={{ fontSize: 12 }} />}>{COPY.slideHidden}</Pill>}
          {locked && <Pill tone="neutral" icon={<LockOutlinedIcon sx={{ fontSize: 12 }} />} title={COPY.slideLocked}>{COPY.slideLocked}</Pill>}
        </Box>
      )}
      {!readOnly && (
        <Box
          className="slide-actions"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            zIndex: 3,
            display: 'flex',
            gap: 0.35,
            p: 0.35,
            borderRadius: 99,
            bgcolor: 'rgba(255,255,255,0.92)',
            opacity: active || selected ? 1 : 0,
          }}
        >
          <IconButton
            size="small"
            title={COPY.slideActions}
            onClick={(event) => {
              event.stopPropagation();
              onOpenMenu(slide.slideId, { x: event.clientX, y: event.clientY });
            }}
            sx={thumbActionSx}
          >
            <MoreVertIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton size="small" title={COPY.duplicateSlide} onClick={() => onDuplicate(slide.slideId)} sx={thumbActionSx}>
            <ContentCopyIcon sx={{ fontSize: 13 }} />
          </IconButton>
          <IconButton
            size="small"
            title={hidden ? COPY.showSlide : COPY.hideSlide}
            onClick={() => onToggleHidden(slide.slideId)}
            sx={thumbActionSx}
          >
            {hidden ? <VisibilityOutlinedIcon sx={{ fontSize: 14 }} /> : <VisibilityOffOutlinedIcon sx={{ fontSize: 14 }} />}
          </IconButton>
          <IconButton
            size="small"
            title={locked ? COPY.unlockSlide : COPY.lockSlide}
            onClick={() => onToggleLock(slide.slideId)}
            sx={thumbActionSx}
          >
            {locked ? <LockOpenOutlinedIcon sx={{ fontSize: 14 }} /> : <LockOutlinedIcon sx={{ fontSize: 14 }} />}
          </IconButton>
          {canDelete && !locked && (
            <IconButton size="small" title={COPY.deleteSlide} onClick={() => onDelete(slide.slideId)} sx={{ ...thumbActionSx, '&:hover': { bgcolor: '#f8e8e6', color: PRES.danger } }}>
              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>
      )}
      </Box>
      <Typography sx={{ display: 'block', mt: 0.5, fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {index + 1}{slide.sectionTitle ? ` · ${slide.sectionTitle}` : ''}
      </Typography>
    </Box>
  );
}

const SlideThumb = React.memo(SlideThumbRow, areSlideThumbPropsEqual);

const SlideStrip: React.FC<SlideStripProps> = ({
  slides,
  activeSlideId,
  selectedSlideIds,
  pageFormat,
  readOnly = false,
  onSelect,
  onAdd,
  onDelete,
  onDuplicate,
  onToggleHidden,
  onToggleLock,
  canSaveToLibrary = false,
  onSaveToLibrary,
  canAddAsTemplate = false,
  onAddAsTemplate,
  canCopyLayoutToKit = false,
  onCopyLayoutToKit,
  onMoveLayoutToKit,
  showPageKindMarks = true,
}) => {
  const canDelete = slides.length > 1;
  const selectedIds = selectedSlideIds?.length
    ? selectedSlideIds
    : (activeSlideId ? [activeSlideId] : []);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const ids = useMemo(() => slides.map((slide) => slide.slideId), [slides]);
  const [width, setWidth] = useState(readStoredStripWidth);
  const widthRef = useRef(width);
  widthRef.current = width;
  const resizeRef = useRef<{ startX: number; startW: number } | null>(null);
  const [menu, setMenu] = useState<{ slideId: string; x: number; y: number } | null>(null);
  const menuSlide = slides.find((slide) => slide.slideId === menu?.slideId) || null;
  const menuDeletesMany = Boolean(menu && selectedSet.has(menu.slideId) && selectedIds.length > 1);
  const menuDeleteCount = menuDeletesMany ? selectedIds.length : 1;
  const menuHasUnlocked = menuDeletesMany
    ? selectedIds.some((id) => !slides.find((slide) => slide.slideId === id)?.locked)
    : !menuSlide?.locked;

  const openMenu = (slideId: string, position: { x: number; y: number }) => {
    setMenu({ slideId, x: position.x, y: position.y });
  };

  const closeMenu = () => setMenu(null);

  return (
    <Box sx={{
      width,
      bgcolor: PRES.surface,
      color: PRES.textPrimary,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      flexShrink: 0,
      position: 'relative',
      borderRight: `1px solid ${PRES.borderSubtle}`,
    }}>
      <Box sx={{ px: 1.5, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.label, color: PRES.textSecondary, fontWeight: 650 }}>
          {selectedIds.length > 1 ? `${selectedIds.length} ${COPY.selectedCount}` : COPY.slides}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          {selectedIds.length > 1 && !readOnly && canDelete && (
            <Button
              size="small"
              color="error"
              onClick={() => onDelete(selectedIds[0])}
              sx={{ minWidth: 0, px: 0.5, textTransform: 'none', ...TYPE.caption, fontWeight: 650 }}
            >
              {deleteSlidesLabel(selectedIds.length)}
            </Button>
          )}
          <Button
            size="small"
            onClick={onAdd}
            disabled={readOnly}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            sx={{ color: PRES.interactive, minWidth: 0, px: 0.5, textTransform: 'none', ...TYPE.caption, fontWeight: 650 }}
          >
            {COPY.addSlide}
          </Button>
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', px: 1.25, pb: 2 }}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {slides.map((slide, index) => (
            <SlideThumb
              key={slide.slideId}
              slide={slide}
              index={index}
              active={slide.slideId === activeSlideId}
              selected={selectedSet.has(slide.slideId)}
              canDelete={canDelete}
              readOnly={readOnly}
              onSelect={onSelect}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onToggleHidden={onToggleHidden}
              onToggleLock={onToggleLock}
              onOpenMenu={openMenu}
              pageFormat={pageFormat}
              showPageKindMarks={showPageKindMarks}
            />
          ))}
        </SortableContext>
      </Box>
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label={COPY.resizeStrip}
        title={COPY.resizeStrip}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            const next = clampStripWidth(widthRef.current + (event.key === 'ArrowRight' ? 16 : -16));
            setWidth(next);
            writeStoredStripWidth(next);
          }
        }}
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          event.currentTarget.setPointerCapture(event.pointerId);
          resizeRef.current = { startX: event.clientX, startW: widthRef.current };
        }}
        onPointerMove={(event) => {
          if (!resizeRef.current) return;
          setWidth(clampStripWidth(resizeRef.current.startW + event.clientX - resizeRef.current.startX));
        }}
        onPointerUp={() => {
          if (!resizeRef.current) return;
          resizeRef.current = null;
          writeStoredStripWidth(widthRef.current);
        }}
        onPointerCancel={() => { resizeRef.current = null; }}
        sx={{
          position: 'absolute',
          top: 0,
          right: -3,
          width: 8,
          height: '100%',
          cursor: 'col-resize',
          zIndex: 5,
          '&:hover, &:focus-visible': { bgcolor: 'rgba(36,93,193,0.18)' },
          ...focusVisibleSx,
        }}
      />
      <Menu
        open={Boolean(menu)}
        onClose={closeMenu}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.y, left: menu.x } : undefined}
      >
        {canSaveToLibrary && onSaveToLibrary && (
          <MenuItem onClick={() => { if (menu) onSaveToLibrary(menu.slideId); closeMenu(); }}>
            {COPY.saveToLibrary}
          </MenuItem>
        )}
        {canAddAsTemplate && onAddAsTemplate && (
          <MenuItem onClick={() => { if (menu) onAddAsTemplate(menu.slideId); closeMenu(); }}>
            {COPY.addAsTemplate}
          </MenuItem>
        )}
        {canCopyLayoutToKit && onCopyLayoutToKit && (
          <MenuItem onClick={() => { if (menu) onCopyLayoutToKit(menu.slideId); closeMenu(); }}>
            {COPY.copyLayoutToKit}
          </MenuItem>
        )}
        {canCopyLayoutToKit && onMoveLayoutToKit && (
          <MenuItem onClick={() => { if (menu) onMoveLayoutToKit(menu.slideId); closeMenu(); }}>
            {COPY.moveLayoutToKit}
          </MenuItem>
        )}
        <MenuItem disabled={readOnly} onClick={() => { if (menu) onDuplicate(menu.slideId); closeMenu(); }}>
          {COPY.duplicateSlide}
        </MenuItem>
        <MenuItem disabled={readOnly} onClick={() => { if (menu) onToggleHidden(menu.slideId); closeMenu(); }}>
          {menuSlide?.hidden ? COPY.showSlide : COPY.hideSlide}
        </MenuItem>
        <MenuItem disabled={readOnly} onClick={() => { if (menu) onToggleLock(menu.slideId); closeMenu(); }}>
          {menuSlide?.locked ? COPY.unlockSlide : COPY.lockSlide}
        </MenuItem>
        <MenuItem
          disabled={readOnly || !canDelete || !menuHasUnlocked}
          onClick={() => { if (menu) onDelete(menu.slideId); closeMenu(); }}
        >
          {deleteSlidesLabel(menuDeleteCount)}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SlideStrip;
